/**
 * Created by Ning Jiang on 2021-06-29.
 */

import {ContinueScreen} from "../../core/screen/ContinueScreen";
import {OpenSans} from "@nolimitcity/slot-launcher/bin/loader/font/OpenSans";
import {FontWeight} from "@nolimitcity/slot-launcher/bin/loader/font/FontStatics";
import {SlotGame} from "@nolimitcity/slot-game/bin/SlotGame";
import {ElGordosRevengeLayers, Layers} from "../view/ElGordosRevengeLayers";
import {ElGordosRevenge} from "../ElGordosRevenge";
import {Translation} from "../../core/utils/Translation";
import {BelowStakeWinRestriction} from "../server/BelowStakeWinRestriction";
import {TimelineLite, TweenLite} from "gsap";
import {ElGordosRevengeSound} from "../sound/ElGordosRevengeSound";
import {BigWinCountUp} from "../winpresentation/presentations/bigwin/BigWinCountUp";
import {GameMode} from "@nolimitcity/slot-game/bin/flowcontrol/GameMode";
import {ElGordosRevengeGameAssets} from "../ElGordosRevengeGameAssets";
import {ElGordosRevengeGameConfig} from "../ElGordosRevengeGameConfig";
import {MathHelper} from "../../core/utils/MathHelper";
import {IWinRatioConfig} from "../../core/config/WinRatio";

class OutroMeterAnchor extends PIXI.Container {

    private _multiOffBrightness = 0.4;
    private _multiUsedBrightness = 0.3;
    private _ropeAnchors = [[0.5, 0.67], [0.5, 0.7], [0.5, 0.66], [0.5, 0.66], [0.5, 0.67]];

    constructor(multiplier:number, index:number, currentLevel:number) {
        super();

        // multiplier
        const multiText = new PIXI.BitmapText(`x${multiplier}`, {
            fontName : ElGordosRevengeGameAssets.BITMAP_FONT_NUDGE_MULTIPLIER,
            fontSize : 20,
            tint : ElGordosRevengeGameConfig.MULTIPLIER_TEXT_TINT
        });
        (<PIXI.Point>multiText.anchor).set(0.5, 1.38);
        multiText.letterSpacing = -12;
        multiText.y = -2;
        multiText.scale.y = 0.7;
        this.addChild(multiText);

        if(index < currentLevel) {
            TweenLite.to(multiText, 0.1, {pixi : {brightness : this._multiUsedBrightness}});
        } else if(index > currentLevel) {
            TweenLite.to(multiText, 0.1, {pixi : {brightness : this._multiOffBrightness}});
        }

        // rope
        if(index > currentLevel) {
            const rope = new PIXI.Sprite(ElGordosRevenge.assetLoader.getTextures(`meter/rope`)[index]);
            rope.anchor.set(this._ropeAnchors[index][0], this._ropeAnchors[index][1]);
            rope.scale.set(0.9);
            this.addChild(rope);
        }

        // scale
        if(index === currentLevel) {
            this.scale.set(1.4);
        }
    }
}

class OutroMeter extends PIXI.Container {

    private _bg:PIXI.Container;
    private _bgSpine:PIXI.spine.Spine | null;
    private _anchors:OutroMeterAnchor[] = [];

    constructor() {
        super();

        this._bg = new PIXI.Container();
        this.addChild(this._bg);
    }

    public init():void {
        this.visible = false;
        this._bgSpine = new PIXI.spine.Spine(ElGordosRevenge.assetLoader.getSpineAsset("meterOutro"));
        this._bgSpine.autoUpdate = true;
        this._bg.addChild(this._bgSpine);
        ElGordosRevenge.model.parsedGameData.fsMeterData.meterMultipliersAfter.forEach((multiplier, index) => {
            const anchor = new OutroMeterAnchor(multiplier, index, ElGordosRevenge.model.parsedGameData.fsMeterData.fsLevel);
            anchor.x = -260 + 130 * index;
            anchor.visible = false;
            this._anchors.push(anchor);
            this.addChild(anchor);
        });
    }

    public show():void {
        this.visible = true;
        const aniName = `step_${ElGordosRevenge.model.parsedGameData.fsMeterData.fsLevel + 1}_start`;
        this._bgSpine!.state.setAnimation(0, aniName, false);
        this._anchors.forEach(anchor => {
            anchor.visible = true;
        });
        TweenLite.to(this, this._bgSpine!.spineData.findAnimation(aniName).duration, {
            onComplete : () => {
                this._bgSpine!.state.setAnimation(0, `step_${ElGordosRevenge.model.parsedGameData.fsMeterData.fsLevel + 1}_loop`, true);
            }
        });
    }

    public reset():void {
        this.visible = false;
        this._anchors.forEach(anchor => {
            this.removeChild(anchor);
            anchor.destroy();
        });
        this._anchors = [];
        this._bgSpine!.state.setEmptyAnimations(0);
        this._bg.removeChild(this._bgSpine!);
        this._bgSpine!.destroy();
        this._bgSpine = null;
    }
}

export class FreespinOutro extends ContinueScreen {

    private _spineHolder:PIXI.Container;
    private _spine:PIXI.spine.Spine | null;
    private _meter:OutroMeter;
    public _countUp:BigWinCountUp;

    constructor() {
        super(ElGordosRevengeLayers.getLayer(Layers.SCREENS)!);

        this._spineHolder = new PIXI.Container();
        this._spineHolder.position.set(360, 360);
        this._container.addChild(this._spineHolder);

        this._played = new PIXI.Text("", new PIXI.TextStyle({
            fill : 0xffffff,
            fontFamily : OpenSans.FAMILY,
            wordWrap : false,
            fontWeight : FontWeight.BOLD,
            fontSize : 28
        }));
        this._played.anchor.set(0.5, 0.5);
        this._played.position.set(360, 590);
        this._played.visible = false;
        this._container.addChild(this._played);

        this._meter = new OutroMeter();
        this._meter.position.set(360, 460);
        this._container.addChild(this._meter);

        this._countUp = new BigWinCountUp(new PIXI.Point(360, 250));
        this._container.addChild(this._countUp);
    }

    public show(onContinueCallBack:() => void):TimelineLite {
        const belowStakeWinRestriction = BelowStakeWinRestriction.isWinBelowStake();
        SlotGame.keypadPlugin.setWin(ElGordosRevenge.model.parsedGameData.totalWin, true, belowStakeWinRestriction);
        SlotGame.keypadPlugin.hide();
        ElGordosRevenge.fsMeter.hide();
        ElGordosRevenge.background.playIdleAnimation();

        if(belowStakeWinRestriction) {
            //TODO: DO NOT play sound and count up, show end number directly.
        }

        const spinPlayed = ElGordosRevenge.model.parsedGameData.numberOfFreespinsPlayed;
        this._played.text = Translation.translation("Spins played: ${spinPlayed}").replace("${spinPlayed}", spinPlayed.toString());
        const spineName = `${ElGordosRevenge.model.data.mode === GameMode.FREESPIN_SUPER ? "sfs" : "fs"}Outro`;
        this._spine = new PIXI.spine.Spine(ElGordosRevenge.assetLoader.getSpineAsset(spineName));
        this._spine.autoUpdate = true;
        this._spineHolder.addChild(this._spine);
        this._spine.state.setAnimation(0, "intro", false);
        this._meter.init();

        return super.show(onContinueCallBack);
    }

    protected getShowAnimations():TimelineLite {
        const noWin = ElGordosRevenge.model.parsedGameData.totalWin === 0 || BelowStakeWinRestriction.isWinBelowStake();
        const tl = new TimelineLite();
        tl.add([
            this._countUp.getCountUpAnimation(0, ElGordosRevenge.model.parsedGameData.totalWin, noWin ? 0.01 : 2),
            () => {
                ElGordosRevenge.initialCountUp.remove();
                ElGordosRevenge.bigWinCountUp.remove();
                ElGordosRevenge.waysWon.remove();
                ElGordosRevengeSound.soundPlayer.playTrack({name : ElGordosRevengeSound.FREE_SPIN_END});
                ElGordosRevengeSound.switchMusic(
                    {name : ElGordosRevengeSound.FREE_SPIN_END_LOOP, volume : 0},
                    {name : ElGordosRevengeSound.FREE_SPIN_END_LOOP, from : 0, to : 1, duration : 1},
                    {name : "", from : 100, to : 0, duration : 400}
                );
                if(!noWin) {
                    ElGordosRevengeSound.soundPlayer.playTrack({name : ElGordosRevengeSound.COUNT_UP_COUNTER});
                }
            }
        ]);
        tl.add(() => {
            this._spine!.state.setAnimation(0, "loop", true);
        }, this._spine!.spineData.findAnimation("intro").duration);
        tl.add(() => {
            this._meter.show();
            this._played.visible = true;
            if(!noWin) {
                ElGordosRevengeSound.soundPlayer.stopTrack(ElGordosRevengeSound.COUNT_UP_COUNTER);
                ElGordosRevengeSound.soundPlayer.playTrack({name : this.getCountUpEndSoundName()});
            }
        });

        return tl;
    }

    private getCountUpEndSoundName():string {
        const data = ElGordosRevenge.model.parsedGameData;
        const totalWinRatio = MathHelper.roundToDecimals(data.totalWin / data.playedBetValue, 2);
        let ratioConfig:IWinRatioConfig;
        const winRatios = ElGordosRevengeGameConfig.getWinRatioConfig(data.mode);
        for(let i:number = winRatios.length - 1; i >= 0; i--) {
            const config = winRatios[i];
            if(totalWinRatio >= config.ratio) {
                ratioConfig = config;
                break;
            }
        }

        switch(ratioConfig!.winLevel) {
            case 4:
                return ElGordosRevengeSound.AS_WIN_BIG;
            case 3:
                return ElGordosRevengeSound.COUNT_UP_END_LARGE;
            case 2:
                return ElGordosRevengeSound.COUNT_UP_END_MEDIUM;
            case 1:
                return ElGordosRevengeSound.COUNT_UP_END_SMALL;
            default:
                return ElGordosRevengeSound.COUNT_UP_END_SMALL;
        }
    }

    protected close():TimelineLite {
        const tl = super.close();
        tl.add(() => {
            ElGordosRevengeSound.soundPlayer.playTrack({name : ElGordosRevengeSound.FREE_SPIN_OUTRO_END});
            ElGordosRevengeSound.switchMusic({name : ElGordosRevengeSound.MAIN_GAME_MUSIC, volume : 1});
            ElGordosRevenge.xBetController.featureVisible = true;
            ElGordosRevenge.background.updateMode(ElGordosRevenge.model.data.nextMode);
        }, 0);
        tl.add(() => {
            this._spine!.state.setEmptyAnimations(0);
            this._spineHolder.removeChild(this._spine!);
            this._spine!.destroy();
            this._spine = null;
            this._meter.reset();
            this._played.visible = false;
        });

        return tl;
    }
}